import { getAvailability } from './get-availability.js'
import { getProgramSummary } from './get-program-summary.js'
import { parseApiDate } from './format-date.js'

const DAY_MS = 24 * 60 * 60 * 1000
const NEW_BADGE_DURATION_DAYS = 14

/* Zmień na true, aby ponownie włączyć automatyczne etykiety „Nowość”. */
const SHOW_NEW_BADGES = false

const CONTINENT_BY_DESTINATION = new Map([
  ['indie', 'Azja'],
  ['szwajcaria', 'Europa'],
])

function normalizeText(value) {
  return typeof value === 'string' ? value.trim() : ''
}

function normalizeLookupKey(value) {
  return normalizeText(value)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase('pl-PL')
}

function toUtcTimestamp(value) {
  const date = parseApiDate(value)

  return date
    ? Date.UTC(date.year, date.month - 1, date.day)
    : null
}

export function getIsoDateInPoland(date = new Date()) {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Warsaw',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date)
  const values = Object.fromEntries(
    parts.map(part => [part.type, part.value])
  )

  return `${values.year}-${values.month}-${values.day}`
}

export function splitProgramName(value) {
  const name = normalizeText(value)
  const match = name.match(/^(.+?)\s+[-–—]\s+(.+)$/u)

  if (!match) return null

  const destination = normalizeText(match[1])
  const title = normalizeText(match[2])

  return destination && title
    ? { destination, title }
    : null
}

export function inferContinent(destination, override = '') {
  const configuredContinent = normalizeText(override)
  if (configuredContinent) return configuredContinent

  return CONTINENT_BY_DESTINATION.get(
    normalizeLookupKey(destination)
  ) ?? ''
}

export function getTripDurationDays(startDate, endDate) {
  const start = toUtcTimestamp(startDate)
  const end = toUtcTimestamp(endDate)

  if (start === null || end === null || end < start) return null

  return Math.round((end - start) / DAY_MS) + 1
}

export function formatDurationDays(days) {
  const value = Number(days)
  if (!Number.isInteger(value) || value < 1) return ''
  if (value === 1) return '1 dzień'
  return `${value} dni`
}

export function isCatalogNew(
  publishedAt,
  today = getIsoDateInPoland()
) {
  const published = toUtcTimestamp(publishedAt)
  const current = toUtcTimestamp(today)

  if (published === null || current === null || current < published) {
    return false
  }

  return (current - published) / DAY_MS < NEW_BADGE_DURATION_DAYS
}

/*
 * Pozwala przygotować program wcześniej i automatycznie odsłonić go
 * w katalogu w dniu zapisanym w `catalog.visibleFrom`.
 */
export function isCatalogVisible(
  catalog = {},
  today = getIsoDateInPoland()
) {
  if (catalog?.enabled === false) return false

  const visibleFrom = normalizeText(catalog?.visibleFrom)
  if (!visibleFrom) return true

  const publication = toUtcTimestamp(visibleFrom)
  const current = toUtcTimestamp(today)

  return publication !== null && current !== null && current >= publication
}

function getBadge(catalog, today) {
  const override = catalog.badgeOverride

  if (typeof override === 'string' && override.trim()) {
    return {
      label: override.trim(),
      variant: catalog.badgeVariant || 'default',
    }
  }

  if (override?.label) {
    return {
      label: String(override.label).trim(),
      variant: override.variant || 'default',
    }
  }

  if (
    SHOW_NEW_BADGES &&
    isCatalogNew(catalog.catalogPublishedAt, today)
  ) {
    return { label: 'Nowość', variant: 'new' }
  }

  return { label: '', variant: 'default' }
}

function getFallbackIdentity(program) {
  const catalog = program.catalog ?? {}
  const destination = normalizeText(
    catalog.destinations?.[0] ??
    program.stickyTitle?.replace(/:\s*$/, '')
  )
  const title = normalizeText(
    catalog.title ?? program.stickySubtitle
  )

  if (destination && title) return { destination, title }

  const parsedLocalTitle = splitProgramName(program.title)
  if (parsedLocalTitle) return parsedLocalTitle

  return {
    destination,
    title: title || normalizeText(program.title),
  }
}

function isCurrentTerm(term, today) {
  const comparisonDate = term.endDate || term.startDate
  return Boolean(comparisonDate) && comparisonDate >= today
}

export function buildCatalogProgram(
  program,
  terms,
  {
    today = getIsoDateInPoland(),
    recommendedOrder = Number.MAX_SAFE_INTEGER,
  } = {}
) {
  const catalog = program.catalog ?? {}
  const currentTerms = terms
    .filter(term => term?.isHidden !== true && isCurrentTerm(term, today))
    .sort((first, second) =>
      String(first.startDate).localeCompare(String(second.startDate))
    )
  const apiIdentity = currentTerms
    .map(term => splitProgramName(term.name))
    .find(Boolean)
  const identity = apiIdentity ?? getFallbackIdentity(program)
  const continent = inferContinent(
    identity.destination,
    catalog.continent
  )
  const mappedTerms = currentTerms.map(term => {
    const availability = getAvailability(term)
    const durationDays = getTripDurationDays(
      term.startDate,
      term.endDate
    )

    return {
      ...term,
      group: term.group || 'Sprawdź szczegóły wyjazdu',
      airport: term.airport || term.departureCity || 'Sprawdź miejsce wyjazdu',
      price: Number.isFinite(Number(term.price?.amount))
        ? Number(term.price.amount)
        : null,
      durationDays,
      duration: formatDurationDays(durationDays),
      availability: availability.label,
      availabilityVariant: availability.variant,
    }
  })
  const summary = getProgramSummary(currentTerms)
  const nearestTerm = mappedTerms[0]
  const firstApiImage = currentTerms.find(
    term => term.thumbnail || term.heroImage
  )
  const badge = getBadge(catalog, today)

  return {
    programKey: program.programKey,
    destination: identity.destination,
    title: identity.title,
    continent,
    travelStyles: Array.isArray(catalog.travelStyles)
      ? [...catalog.travelStyles]
      : [],
    image:
      catalog.image ||
      firstApiImage?.thumbnail ||
      firstApiImage?.heroImage ||
      catalog.heroImageFallback ||
      '',
    fallbackImage:
      catalog.heroImageFallback ||
      firstApiImage?.heroImage ||
      '',
    imagePosition: catalog.imagePosition || 'center center',
    imageScale: catalog.imageScale || 1,
    imageAlt: `${identity.destination} – ${identity.title}`,
    durationDays: nearestTerm?.durationDays ?? null,
    duration: nearestTerm?.duration || '',
    label: badge.label,
    labelVariant: badge.variant,
    priceFrom: summary.lowestPrice
      ? Number(summary.lowestPrice.amount)
      : null,
    pricesDiffer: summary.pricesDiffer,
    pageUrl: program.pageUrl,
    publishedAt: catalog.catalogPublishedAt || '',
    recommendedOrder,
    terms: mappedTerms,
  }
}
